import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuCircleCheck, LuCirclePause, LuCirclePlus, LuHourglass, LuLandmark } from 'react-icons/lu';
import DataCardList from '../../components/data/DataCardList';
import DataTable from '../../components/data/DataTable';
import DataToolbar from '../../components/data/DataToolbar';
import FilterSheet from '../../components/data/FilterSheet';
import PageHero from '../../components/data/PageHero';
import StatTiles from '../../components/data/StatTiles';
import TablePagination from '../../components/data/TablePagination';
import Button from '../../components/ui/Button';
import useElementWidth from '../../hooks/useElementWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import usePagedList from '../../hooks/usePagedList';
import useTableColumns from '../../hooks/useTableColumns';
import useViewportFit from '../../hooks/useViewportFit';
import BankAccountFormModal from './bankAccounts/BankAccountFormModal';
import useBankAccounts from './bankAccounts/useBankAccounts';
import useBankAccountText from './bankAccounts/bankAccountText';
import {
  ACCOUNT_COLUMNS,
  ALL_GROUPS,
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  getFilterState,
} from './bankAccounts/bankAccountColumns';
import BankAccountCell, { BankAccountCardRow } from './bankAccounts/parts/BankAccountCell';
import BankAccountDetailPane from './bankAccounts/parts/BankAccountDetailPane';

const COLUMNS_STORAGE_KEY = 'bankAccounts.tableColumns.v1';
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Banka Hesap Tanımlama (Tailwind), built like the other data pages: status
 * tiles that filter, a table card that reaches at most the bottom of the
 * screen (search, status switch, header filters, column menu), the selected
 * account's panel beside it and one dialog for adding and editing.
 * The data logic lives in useBankAccounts.
 */
const BankAccountManagementPage = () => {
  const {
    bankAccounts, isLoading, filteredAccounts, stats, searchTerm, setSearchTerm, filters, handleFilter, clearFilters,
    statusGroup, setStatusGroup, selectedAccount, setSelectedId, saveAccount,
  } = useBankAccounts();
  const { language, locale, label } = useBankAccountText();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: ACCOUNT_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  const maxCardHeight = !compactList && fitHeight ? fitHeight : undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [form, setForm] = useState({ open: false, account: null, key: 0 });
  const hasLoaded = bankAccounts.length > 0 || !isLoading;

  const resetKey = JSON.stringify([filters, searchTerm, statusGroup]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredAccounts, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  // Escape closes the panel, unless a dialog or field has it
  useEffect(() => {
    if (!selectedAccount) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedId(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedAccount, showFilterSheet, setSelectedId]);

  const columnLabel = useCallback((key) => label(`fields.${key}`), [label]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: { type: ACCOUNT_COLUMNS[key].type },
  })), [columns.visibleKeys, columnLabel]);

  const filterChips = Object.keys(filters)
    .filter((key) => ACCOUNT_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => ({ key, label: columnLabel(key), value: String(filters[key]), state: 'applied' }));

  const isFilterActive = Object.values(filters).some(Boolean) || Boolean(searchTerm) || statusGroup !== ALL_GROUPS;

  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedAccount ? filteredAccounts.findIndex((account) => account.id === selectedAccount.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredAccounts[selectedIndex + offset];
    if (!sibling) return;
    setSelectedId(sibling.id);
    reveal(sibling.id);
  };

  const openForm = (account = null) => setForm((current) => ({ open: true, account, key: current.key + 1 }));
  const closeForm = () => setForm((current) => ({ ...current, open: false }));
  const toggleGroup = (group) => setStatusGroup(statusGroup === group ? ALL_GROUPS : group);

  const shareOf = (count) => (stats.total > 0 ? Math.min(1, count / stats.total) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const tiles = [
    { key: 'total', label: label('stats.total'), shortLabel: label('stats.totalShort'), value: stats.total, icon: LuLandmark, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30', onClick: clearFilters, active: !isFilterActive },
    { key: 'active', label: label('stats.active'), shortLabel: label('stats.activeShort'), value: stats.active, icon: LuCircleCheck, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', share: shareOf(stats.active), shareTitle: shareTitle(stats.active), barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500', onClick: () => toggleGroup('active'), active: statusGroup === 'active' },
    { key: 'passive', label: label('stats.passive'), shortLabel: label('stats.passiveShort'), value: stats.passive, icon: LuCirclePause, gradient: 'tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30', onClick: () => toggleGroup('passive'), active: statusGroup === 'passive' },
    { key: 'pending', label: label('stats.pending'), shortLabel: label('stats.pendingShort'), value: stats.pending, icon: LuHourglass, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30', onClick: () => toggleGroup('pending'), active: statusGroup === 'pending' },
  ];

  const paneProps = {
    account: selectedAccount,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredAccounts.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredAccounts.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedId(null),
    onEdit: () => openForm(selectedAccount),
  };

  const emptyLabel = !isLoading && bankAccounts.length === 0 ? label('table.none') : label('table.empty');

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <PageHero
          icon={LuLandmark}
          gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30"
          title={label('page.title')}
          subtitle={label('page.subtitle')}
          actionsClassName="tw-flex tw-items-center tw-gap-2 max-sm:tw-w-full"
        >
          <Button variant="primary" onClick={() => openForm(null)} className="max-sm:tw-w-full">
            <LuCirclePlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('actions.add')}
          </Button>
        </PageHero>

        <StatTiles tiles={tiles} ready={hasLoaded} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: 1,
              }}
              status={{
                options: [ALL_GROUPS, 'active', 'passive', 'pending'].map((value) => ({ value, label: label(`toolbar.groups.${value}`) })),
                value: statusGroup,
                onChange: setStatusGroup,
                layoutId: 'bank-account-status',
                label: label('toolbar.status'),
              }}
              columnMenu={{
                columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(account) => <BankAccountCardRow account={account} />}
                selectedId={selectedAccount?.id}
                onSelect={(account) => setSelectedId(account.id)}
                isLoading={isLoading}
                isBusy={false}
                emptyLabel={emptyLabel}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(account, key) => <BankAccountCell columnKey={key} account={account} />}
                getCellTitle={(account, key) => (account[key] ? String(account[key]) : undefined)}
                selectedId={selectedAccount?.id}
                onSelect={(account) => setSelectedId(account.id)}
                isLoading={isLoading}
                isBusy={false}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={emptyLabel}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={filteredAccounts.length}
              rangeOf={label('table.rangeOf', { total: filteredAccounts.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: stats.total.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={false}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <BankAccountDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <BankAccountDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key), type: ACCOUNT_COLUMNS[key].type }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredAccounts.length}
      />

      <BankAccountFormModal
        key={form.key}
        isOpen={form.open}
        account={form.account}
        onClose={closeForm}
        onSave={saveAccount}
      />
    </>
  );
};

export default BankAccountManagementPage;
